import { useAtom } from "jotai"
import { atomHasNewAnnouncement } from "../states"
import useSWR from "swr"
import { blinkoAnnouncementStorage } from "~/core/storages"
import { api } from "../api"
import { icons } from "../assets/icons/icons";
import { useSound } from "../components/SoundHandler";
import { ContainerShape, HexagonShape } from "../components/Shape";
import { useEffect } from "react"
import { useModal } from "~/core/contexts/ModalContext"
import { motion } from "framer-motion"

export const AnnouncementModal = ({ hasNew, onDismiss }: { hasNew?: boolean, onDismiss: () => void }) => {
  const sounds = useSound()
  const { data } = useAnnouncement()

  const onClose = () => {
    onDismiss()
    sounds.play('click')
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#18181B]/50">
      <div>
        <ContainerShape {...{cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 }}>
          <div className="p-6 w-[350px] md:w-[448px]">
            <div className="flex flex-row justify-between">
              <div className="mt-1 text-2xl font-blinko font-black text-white">
                Announcement
              </div>

                <button className="" onClick={onClose}>
                <HexagonShape cornerRadius={6} strokeWidth={1}>
                  <div className="flex justify-center items-center p-2">
                    <img src={icons.close} className='w-6 h-6' />
                  </div>
                </HexagonShape>
              </button>
              
            </div>

            <div className="flex flex-col gap-1 mt-3">
              {data?.map((announcement, i) => (
                <motion.div
                  key={announcement.id}
                  initial={(hasNew && i === 0) ? {
                    background: 'rgba(255, 255, 255, 0.5)',
                  } : {
                  }}
                  animate={(hasNew && i === 0) ? {
                    background: 'rgba(255, 255, 255, 0.05)',
                  } : {
                  }}
                  transition={{ duration: 1 }}
                  className="flex flex-col p-2 rounded-lg bg-white/5"
                >
                  <p className="text-xs text-white/50">{new Date(announcement.date).toLocaleDateString()}</p>
                  <p className="text-base font-blinko text-white">{announcement.title}</p>
                  <p className="text-sm text-white/50">{announcement.description}</p>
                </motion.div>
              ))}
            </div>

          </div>
        </ContainerShape>
      </div>
    </div>
  );
}

export const useAnnouncement = () => {
  const { showModal } = useModal()
  const [hasNew, setHasNew] = useAtom(atomHasNewAnnouncement)

  useEffect(() => {
    if (hasNew) {
      sync()
      showModal(hide => <AnnouncementModal hasNew onDismiss={hide} />, { hideOnEscapeKey: true })
    }
  }, [hasNew])
 
  const { data: announcement } = useSWR('announcement', async () => {
    const announcement = await api.getAnnouncement()
    announcement.sort((a, b) => a.date && b.date ? new Date(b.date).getTime() - new Date(a.date).getTime() : 0)

    const storedAnnouncement = blinkoAnnouncementStorage.get()
    if (storedAnnouncement) {
      const storedAnnouncementData = storedAnnouncement.split(',')
      const newAnnouncement = announcement.filter(x => !storedAnnouncementData.includes(x.id))
      if (newAnnouncement.length > 0) {
        setHasNew(true)
      }
    } else {
      setHasNew(true)
    }
    return announcement
  })

  const sync = () => {
    if (announcement) {
      blinkoAnnouncementStorage.set(announcement.map(x => x.id).join(','))
      setHasNew(false)
    }
  }

  return { data: announcement || [], hasNew, sync }
}